import { TamediaStyles } from '@ta-interaktiv/microfrontend-template-helpers'
import { createContext, FC, ReactElement, useContext } from 'react'
import styled from 'styled-components'
// import of some global styles can be deleted if you don't use them
import colorVariables from '../../Globals/colors.module.css'
import fontVariables from '../../Globals/fonts.module.css'

import { TextWidth, TextWidthExtended } from '@ta-interaktiv/react-disco-grid'

import { Scroller } from '@ta-interaktiv/react-scroller'
import { BodyComponent } from 'components/BodyComponent'
import { ChartFrame } from 'components/ChartFrame'

import Text_DE from '../../Localization/Text_DE.json'
import Text_FR from '../../Localization/Text_FR.json'
import { AbbauChartt } from 'components/AbbauChart'
import { SingleFact, SingleFactWrapper } from 'components/SingleFact/SingleFact'
import { AgeChart } from 'components/AgeChart'
import { PromilleRechner } from 'components/PromilleRechner'
import { Aufnahmefaktoren } from 'components/Aufnahmefaktoren'
import { Lauftext_DE } from 'Localization/Lauftext_DE'
import { useWindowSize } from 'usehooks-ts'
import { Consumption } from 'components/Consumption'
import { Age } from 'components/Age'
import { Lauftext_FR } from 'Localization/Lauftext_FR'
import { MFContextProvider } from 'components/MFHelpers/MFContext'
import { PartSplitter } from 'components/MFHelpers'

export const LocaleContext = createContext(
  Text_DE as { [key: string]: string | string[] }
)
export const LauftextContext = createContext(Lauftext_DE as JSX.Element[])

const isdev = window.location.hostname === 'localhost'

const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode,
  properties,
}): ReactElement => {
  const globalClasses = [
    colorVariables.globalColors,
    fontVariables.globalFonts,
    colorMode === 'dark' ? colorVariables.globalColorsDark : '',
  ]
  const { width } = useWindowSize()
  const alkoholcolor = colorMode === 'dark' ? '#FFE462' : '#2CA29F'
  const bodycolor = colorMode === 'dark' ? '#7D7D7D' : '#7D7D7D'
  const headcolor = colorMode === 'dark' ? '#7D7D7D' : '#D4D4D4'
  const bgcolor = colorMode === 'dark' ? '#282828' : '#E9E9E9'
  const TEXT = tenant && tenant.lang === 'fr' ? Text_FR : Text_DE
  const LAUFTEXT = tenant && tenant.lang === 'fr' ? Lauftext_FR : Lauftext_DE //useContext(LauftextContext)

  const part: 'one' | 'two' | 'three' =
    properties && ['one', 'two', 'three'].includes(properties.part)
      ? (properties.part as 'one' | 'two' | 'three')
      : 'one'

  return (
    <AppContainer
      $colorMode={colorMode}
      className={globalClasses.join(' ')}
      style={{
        lineHeight: 0,
        backgroundColor: colorMode === 'dark' ? '#121212' : '#fff',
      }}
    >
      <LocaleContext.Provider value={TEXT}>
        <PartSplitter
          currentPart={part}
          parts={{
            one: (
              <>
                <TextWidth>
                  <TextContainer>{LAUFTEXT[0]}</TextContainer>
                </TextWidth>
                <TextWidth>
                  <ChartFrame
                    title={TEXT.consumption[5]}
                    source={TEXT.consumption[6]}
                  >
                    <Consumption
                      colorMode={colorMode}
                      data={[
                        {
                          label: TEXT.consumption[0],
                          value: 2.9,
                          unit: 'l',
                          color: alkoholcolor,
                        },
                        {
                          label: TEXT.consumption[1],
                          value: 3.7,
                          unit: 'l',
                          color: alkoholcolor,
                        },
                        {
                          label: TEXT.consumption[2],
                          value: 1.4,
                          unit: 'l',
                          color: alkoholcolor,
                        },
                        {
                          label: TEXT.consumption[3],
                          value: 0.1,
                          unit: 'l',
                          color: alkoholcolor,
                        },
                        {
                          label: TEXT.consumption[4],
                          value: 8,
                          unit: 'l',
                          color: bodycolor,
                        },
                      ]}
                    />
                  </ChartFrame>
                </TextWidth>
              </>
            ),
            two: (
              <>
                <TextWidth>
                  <TextContainer>{LAUFTEXT[1]}</TextContainer>
                </TextWidth>
                <TextWidth>
                  <ChartFrame
                    title={TEXT.agechart[0]}
                    sub={TEXT.agechart[1]}
                    source={TEXT.agechart[2]}
                  >
                    <Age
                      colorMode={colorMode}
                      data={[
                        {
                          label: TEXT.agechart[3],
                          value: [4.9, 9.6, 32],
                        },
                        {
                          label: TEXT.agechart[4],
                          value: [12.4, 17.5, 34.8],
                        },
                      ]}
                    />
                  </ChartFrame>
                </TextWidth>
              </>
            ),
            three: (
              <>
                <TextWidth>
                  <TextContainer>{LAUFTEXT[2]}</TextContainer>
                </TextWidth>
                <Scroller
                  front={[
                    <FrontElement id={'first'} transparent wide>
                      <ChapterHead>{LAUFTEXT[3]}</ChapterHead>
                    </FrontElement>,
                    <FrontElement id={'second'} distanceBottom={900}>
                      <TextContainer>{LAUFTEXT[4]}</TextContainer>
                    </FrontElement>,
                    <FrontElement id={'third'} distanceBottom={900} wide>
                      <TextContainer>
                        {LAUFTEXT[5]}
                        <Aufnahmefaktoren colorMode={colorMode} />
                        {LAUFTEXT[6]}
                      </TextContainer>
                    </FrontElement>,
                    <FrontElement id={'fourth'} distanceBottom={900} wide>
                      <TextContainer>
                        {LAUFTEXT[7]}
                        <ChartFrame
                          title={TEXT.abbauchart[0]}
                          sub={TEXT.abbauchart[1]}
                          source={TEXT.abbauchart[3]}
                        >
                          <AbbauChartt
                            hlColor={bodycolor}
                            curveColor={alkoholcolor}
                          />
                        </ChartFrame>
                      </TextContainer>
                    </FrontElement>,
                    <FrontElement id={'fifth'} distanceBottom={900} wide>
                      <TextContainer>{LAUFTEXT[8]}</TextContainer>
                    </FrontElement>,
                    <FrontElement id={'sixt'} transparent wide>
                      <ChapterHead>{LAUFTEXT[9]}</ChapterHead>
                    </FrontElement>,
                  ]}
                  back={(a: number, i: string) => {
                    return (
                      <BodyComponent currentView={i} colorMode={colorMode} />
                    )
                  }}
                />
                <div
                  style={{
                    lineHeight: '0',
                    paddingTop: '60px',
                  }}
                >
                  <TextWidth>
                    <TextContainer>{LAUFTEXT[10]}</TextContainer>
                  </TextWidth>

                  <TextWidth>
                    <TextContainer>{LAUFTEXT[11]}</TextContainer>
                  </TextWidth>
                  <TextWidth>
                    <TextContainer>
                      {LAUFTEXT[12]}
                      <div
                        style={{
                          textAlign: 'center',
                          marginTop: '0px',
                          marginBottom: '0px',
                          maxWidth: '100%',
                        }}
                      >
                        <img
                          src={
                            tenant && tenant.lang === 'fr'
                              ? require('../../assets/bodyicons_formula_fr.png')
                              : require('../../assets/bodyicons_formula.png')
                          }
                          style={{
                            maxWidth: width < 712 ? '100%' : '70%',
                          }}
                        />
                      </div>
                      <ChartFrame>
                        <PromilleRechner colorMode={colorMode} />
                      </ChartFrame>
                    </TextContainer>
                  </TextWidth>
                  {/* <TextWidth>
              <ChartFrame>
                <PromilleTabelle
                  alcoholcolor={alkoholcolor}
                  headcolor={headcolor}
                  bgcolor={bgcolor}
                />
              </ChartFrame>
            </TextWidth> */}

                  <TextWidth>
                    <TextContainer>{LAUFTEXT[13]}</TextContainer>
                  </TextWidth>
                  <Scroller
                    front={[
                      <FrontElement id={'seventh'} transparent wide>
                        <ChapterHead>{LAUFTEXT[14]}</ChapterHead>
                      </FrontElement>,
                      <FrontElement id={'seventh'}>
                        <TextContainer>{LAUFTEXT[15]}</TextContainer>
                      </FrontElement>,
                      <FrontElement id={'eight'} distanceBottom={900} wide>
                        <TextContainer>{LAUFTEXT[16]}</TextContainer>
                      </FrontElement>,
                      <FrontElement id={'eight'} distanceBottom={900} wide>
                        <TextContainer>{LAUFTEXT[17]}</TextContainer>
                      </FrontElement>,
                      <FrontElement id={'eight'} distanceBottom={900} wide>
                        <TextContainer>{LAUFTEXT[18]}</TextContainer>
                      </FrontElement>,
                      <FrontElement
                        id={'nine'}
                        distanceBottom={500}
                        wide
                        transparent
                        centered
                      >
                        <ChapterHead>{LAUFTEXT[19]}</ChapterHead>
                      </FrontElement>,
                      <FrontElement id={'ten'} distanceBottom={1900} wide>
                        <TextContainer>{LAUFTEXT[20]}</TextContainer>
                      </FrontElement>,
                    ]}
                    back={(a: number, i: string) => (
                      <BodyComponent currentView={i} colorMode={colorMode} />
                    )}
                  />
                </div>
                <TextWidth>
                  <div style={{ lineHeight: '30px', marginTop: '60px' }}>
                    {LAUFTEXT[21]}
                  </div>
                </TextWidth>
                <TextWidth>
                  <ChartFrame
                    title={TEXT.verkehrschart[0]}
                    sub='2020/2021'
                    source={TEXT.verkehrschart[5]}
                  >
                    <SingleFactWrapper>
                      <SingleFact
                        value={11982}
                        label={TEXT.verkehrschart[1]}
                        icon={require('../../assets/bodyicons_licence' +
                          (colorMode === 'dark' ? '_dm' : '') +
                          '.png')}
                      />
                      <SingleFact
                        value={3320}
                        label={TEXT.verkehrschart[2]}
                        icon={require('../../assets/bodyicons_accident' +
                          (colorMode === 'dark' ? '_dm' : '') +
                          '.png')}
                      />
                      <SingleFact
                        value={1832}
                        label={TEXT.verkehrschart[3]}
                        icon={require('../../assets/bodyicons_hurt' +
                          (colorMode === 'dark' ? '_dm' : '') +
                          '.png')}
                      />
                      <SingleFact
                        value={28}
                        label={TEXT.verkehrschart[4]}
                        icon={require('../../assets/bodyicons_death' +
                          (colorMode === 'dark' ? '_dm' : '') +
                          '.png')}
                      />
                    </SingleFactWrapper>
                  </ChartFrame>
                </TextWidth>
                <TextWidth>
                  <SourceBox
                    style={{
                      backgroundColor:
                        colorMode === 'dark' ? bodycolor : 'var(--gray-6)',
                    }}
                  >
                    <b>{TEXT.sources[0]}:</b> {TEXT.sources[1]}
                  </SourceBox>
                </TextWidth>
              </>
            ),
          }}
          placeHolderHeights={{
            one: 100,
          }}
        ></PartSplitter>
      </LocaleContext.Provider>
    </AppContainer>
  )
}

const SourceBox = styled.div`
  line-height: 1.4em;
  padding: 10px;
  font-size: 15px;
  font-family: var(--font-sans-serif);
`
const TextContainer = styled.div`
  line-height: 1.4em !important;
  max-width: 100%;
  overflow: hidden;
`

const FrontElement = styled.div<{
  distanceBottom?: number
  hidden?: boolean
  wide?: boolean
  transparent?: boolean
  inverted?: boolean
  centered?: boolean
}>`
  opacity: ${({ hidden }) => (hidden ? 0 : 1)};
  background-color: ${({ transparent }) =>
    transparent ? 'transparent' : ' var(--site-background)'};

  max-width: 700px;
  padding: 15px;
  margin-left: auto;
  margin-right: auto;
  text-align: ${({ centered }) => (centered ? 'center' : 'left')};
  color: ${({ inverted }) => (inverted ? '#fff' : 'unset')};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -15px;
    left: 0;
    height: 15px;
    width: 100%;
    display: inline-block;
    background: rgba(32, 35, 70, 0);
    background: ${({ transparent }) =>
      transparent
        ? 'unset'
        : 'linear-gradient(180deg,rgba(32, 35, 70, 0) 0%,rgba(32, 35, 70, 0.05) 100%);'};
  }
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 0;
    height: 15px;
    width: 100%;
    display: inline-block;
    background: rgba(32, 35, 70, 0);
    background: ${({ transparent }) =>
      transparent
        ? 'unset'
        : 'linear-gradient(180deg,rgba(32, 35, 70, 0.05) 0%,rgba(32, 35, 70, 0) 100%);'};
  }
`

const ChapterHead = styled.h1<{
  inverted?: boolean
}>`
  max-width: 100%;
  /* overflow: hidden; */
  margin: 0;
  padding: 0;
  font-family: 'Suez One', serif;
  letter-spacing: 2px;
  font-size: 60px;
  text-align: center;
  color: ${({ inverted }) => (inverted ? '#fff' : 'unset')};
  @media only screen and (max-width: 711px) {
    font-size: 40px;
    letter-spacing: 1px;
  }
`

const AppContainer = styled(TamediaStyles)`
  font-family: var(--font-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  p {
    line-height: 30px;
  }
  a {
    text-decoration: none;
    background-image: ${({ $colorMode }) =>
      $colorMode === 'light'
        ? 'linear-gradient(90deg, #ffe462, #ffe462)'
        : 'linear-gradient(90deg, #9698A8, #9698A8)'};
    background-repeat: repeat-x;
    background-position: bottom;
    background-size: 100% 20%;
    transition: all 0.1s ease-in-out;
    color: inherit !important;
    background-origin: content-box;
    &:hover {
      background-size: 100% 100%;
    }
    .linkicon {
      rect {
        fill: var(--text-color);
      }
    }
  }
`
export default MainComponent
